<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import WeatherWidget from "../components/widgets/WeatherWidget.vue";
import TimeWidget from "../components/widgets/TimeWidget.vue";
import NewsWidget from "../components/widgets/NewsWidget.vue";
import UserWidget from "../components/widgets/UserWidget.vue";
import CalendarWidget from "../components/widgets/CalendarWidget.vue";

const { logout, user, isAuthenticated } = useAuth0();

const handleLogout = () => {
  logout({ logoutParams: { returnTo: window.location.origin } });
};
</script>

<template>
  <div class="grid-x full-width">
    <div class="cell small-12 medium-offset-1 medium-10 large-offset-1 large-10 white-section content-section">
      <div class="grid-x">
        <div class="cell medium-3 show-for-medium-only"></div>
        
        <!-- User Sidebar -->
        <div class="cell small-12 medium-6 large-3">
          <UserWidget :user="user" @logout="handleLogout" />
        </div>

        <!-- Main Dashboard Section -->
        <div class="cell small-12 medium-12 large-9">
          <div class="main-content">
            <h2 class="left-align">
              Hi {{ user?.meta_firstname || user?.name || 'there' }} 
              <span class="page-badge">Home</span>
            </h2>

            <p>
              Welcome to your personal dashboard. Your central hub for staying connected to what matters around you.
              Designed to bring essential everyday information together in one place, this space automatically adapts to
              your location to keep you informed with minimal effort.
            </p>
            <p>
              Whether you're checking the local weather forecast before heading out, confirming accurate local time zone
              details, or skimming the latest regional headlines, everything is structured for quick, clear reading.
              Explore your widgets below to get a real-time snapshot of your day.
            </p>

            <h3 class="left-align">Local information</h3>

            <!-- Row 1: Equal-Height Widgets -->
            <div class="grid-x align-stretch widget-row">
              <div class="cell small-12 medium-offset-3 medium-6 large-offset-0 large-4 margin-bottom card-padding">
                <TimeWidget :user-location="user?.meta_location.address_line3 || 'London'" />
              </div>
              <div class="cell small-12 medium-offset-3 medium-6 large-4 large-offset-0 margin-bottom card-padding">
                <WeatherWidget :user-location="user?.meta_location.address_line3 || 'London'" />
              </div>
              <div class="cell small-12 medium-offset-3 medium-6 large-offset-0 large-4 margin-bottom card-padding">
                <NewsWidget :user-location="user?.meta_location.address_line3 || 'London'" />
              </div>
            </div>

            <h3 class="left-align">Your information</h3>

            <!-- Row 2: User Utilities -->
            <div class="grid-x">
              <div class="cell small-12 medium-offset-3 medium-6 large-offset-0 large-6 margin-bottom card-padding">
                <CalendarWidget />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/abstract/variables" as *;

h2 {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 30px;
  font-weight: 700;

  .page-badge {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(211, 84, 0, 0.3);
    color: #d35400;

    font-size: 13px;
    font-weight: 800;
    padding: 3px 12px;
    border-radius: 50px;

    text-transform: uppercase;
    letter-spacing: 0.8px;
    line-height: 1;
    transform: translateY(1px);
  }
}

h3 {
  font-size: 24px;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.main-content {
  padding: 1rem;
}

/* Forces Foundation grid cells and Vue component roots to stretch equally */
.widget-row {
  align-items: stretch;

  .card-padding {
    display: flex;
    flex-direction: column;

    > * {
      flex: 1;
      height: 100%;
    }
  }
}

@include desktop {
  .card-padding {
    padding: 10px;
  }
}

$global-radius: 10px;
$white: rgba(255, 255, 255, 0.5);
$light-gray: #{$primary-color}+"66";

$card-user-container-margin-bottom: 1rem;
$card-user-container-radius: $global-radius;
$card-user-container-background: $white;
$card-user-container-color: $primary-color;
$card-user-container-image-size: calc(190px);

.card-user-container {
  margin: $card-user-container-margin-bottom;
  background-color: $card-user-container-background;
  color: $secondary-color;
  overflow: hidden;
  padding: 0;
  border-radius: $card-user-container-radius;

  .card-user-avatar {
    .user-image {
      display: block;
      margin: auto;
      border-radius: 50%;
      width: $card-user-container-image-size;
      height: $card-user-container-image-size;
      padding: calc(15px);
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .card-user-bio {
    padding: 1rem;
    text-align: center;

    .location {
      display: inline-block;
      margin: 0 calc(15px);
      color: $secondary-color;
      font-size: calc(14px);
    }

    .location-icon,
    .location-text {
      display: block;
      padding: 0 calc(5px);
    }
  }

  .card-user-social {
    background: $light-gray;
    padding: 1rem;
    text-align: center;

    ul {
      display: inline-block;

      li {
        margin: 0 10px;
        font-size: 1.2em;
        display: inline;
        cursor: pointer;

        svg:hover {
          color: $primary-color;
          transition: transform .2s;
          transform: scale(1.5);
        }
      }
    }
  }

  .card-user-button {
    text-align: center;
    padding: 1rem;
  }
}
</style>